import { useCallback, useEffect, useRef, useState } from 'react';
import useEventCallback from './hooks/use-event-callback';
import isRightClick from '../utils/is-right-click';
import { useAtomValue } from 'jotai';
import {
  isSkippingAtom,
  validatedTargetPositionAtom
} from '../player-controls-navigation/skip-buttons';
import { getProgressPercentage } from './player-controls-scrubber-utils';
import { usePlayerContext } from '@smart-tv/components/player/_contexts/player-context';
import { StreamType } from '@smart-tv/typings/content';
import useStream from '@smart-tv/components/player/_hooks/use-stream';
import {
  isDownKey,
  isEndKey,
  isHomeKey,
  isLeftKey,
  isRightKey,
  isUpKey
} from '@smart-tv/utils/remoteKeys';
import {
  isSliderFocusedAtom,
  isScrubberDraggingAtom,
  dragSkipPositionAtom
} from './player-controls-scrubber-base';

type Props = {
  min: number;
  max: number;
  step: number;
  progressPercentage: number;
  onChange?: (value: number) => void;
  onChangeCommitted?: (value: number) => void;
  onMouseMove?: (value: number) => void;
  onMouseLeave?: (event: MouseEvent | TouchEvent) => void;
  rounded?: boolean;
};

const useSlider = (props: Props) => {
  const {
    min,
    max,
    step,
    progressPercentage,
    onChange,
    onChangeCommitted,
    onMouseMove,
    onMouseLeave,
    rounded = false
  } = props;
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const sliderTrackRef = useRef<HTMLDivElement | null>(null);

  const touchIdRef = useRef<number | null>(null);

  const { player } = usePlayerContext();

  const { streamType, stream: { duration: durationSeconds = 0 } = {} } = useStream();

  const isSkipping = useAtomValue(isSkippingAtom);

  const validatedTargetPoisition = useAtomValue(validatedTargetPositionAtom);

  const [isStartover, setIsStartover] = useState(false);

  const isSliderFocused = useAtomValue(isSliderFocusedAtom);
  const isScrubberDragging = useAtomValue(isScrubberDraggingAtom);
  const dragSkipPosition = useAtomValue(dragSkipPositionAtom);

  // https://www.w3.org/TR/wai-aria-practices-1.2/#slider_kbd_interaction
  const handleKeyDown = useEventCallback((event: KeyboardEvent) => {
    event.preventDefault();

    let nextValue: number;

    if (isLeftKey(event) || isDownKey(event)) {
      nextValue = progressPercentage - step;
    } else if (isRightKey(event) || isUpKey(event)) {
      nextValue = progressPercentage + step;
    } else if (isHomeKey(event)) {
      nextValue = min;
    } else if (isEndKey(event)) {
      nextValue = max;
    } else {
      return;
    }

    const roundedValue = roundValueToStep(nextValue, step, min);
    const clampedValue = clampValue(roundedValue, min, max);

    if (onChange) {
      onChange(clampedValue);
    }
  });

  const getNextValueFromEvent = useCallback(
    (event: TouchEvent | MouseEvent) => {
      return getValueFromEvent(event, sliderTrackRef.current, touchIdRef.current, {
        min,
        max,
        step,
        rounded
      });
    },
    [min, max, step, rounded]
  );

  const handleSlideStart = useEventCallback((event: TouchEvent | MouseEvent) => {
    if (isRightClick(event)) return;

    if ((event as TouchEvent).changedTouches) {
      event.preventDefault();
      const touch = (event as TouchEvent).changedTouches?.[0];

      if (touch !== null) {
        touchIdRef.current = touch.identifier;
      }
    }

    const nextValue = getNextValueFromEvent(event);

    if (onChange && nextValue !== null) {
      onChange(nextValue);
    }

    document.addEventListener('mousemove', handleSlideMove);
    document.addEventListener('mouseup', handleSlideStop);
    document.addEventListener('touchmove', handleSlideMove);
    document.addEventListener('touchend', handleSlideStop);
  });

  const handleSlideMove = useEventCallback((event: MouseEvent | TouchEvent) => {
    const nextValue = getNextValueFromEvent(event);

    if (onChange && nextValue !== null) {
      onChange(nextValue);
    }
  });

  const handleSlideStop = useEventCallback((event: MouseEvent | TouchEvent) => {
    touchIdRef.current = null;

    const nextValue = getNextValueFromEvent(event);

    if (onChangeCommitted && nextValue !== null) {
      onChangeCommitted(nextValue);
    }

    document.removeEventListener('mousemove', handleSlideMove);
    document.removeEventListener('mouseup', handleSlideStop);
    document.removeEventListener('touchmove', handleSlideMove);
    document.removeEventListener('touchend', handleSlideStop);
  });

  const handleMouseMove = useEventCallback((event: MouseEvent | TouchEvent) => {
    const value = getValueFromEvent(event, sliderTrackRef.current, touchIdRef.current, {
      min,
      max,
      step,
      rounded
    });

    if (!value) return;

    onMouseMove?.(value);
  });

  const handleMouseLeave = useEventCallback((event: MouseEvent | TouchEvent) => {
    onMouseLeave?.(event);
  });

  useEffect(() => {
    const { current: slider } = sliderRef;

    slider?.addEventListener('mousedown', handleSlideStart);
    slider?.addEventListener('touchstart', handleSlideStart);
    slider?.addEventListener('keydown', handleKeyDown);
    slider?.addEventListener('mousemove', handleMouseMove);
    slider?.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      slider?.removeEventListener('mousedown', handleSlideStart);
      slider?.removeEventListener('touchstart', handleSlideStart);
      slider?.removeEventListener('keydown', handleKeyDown);
      slider?.removeEventListener('mousemove', handleMouseMove);
      slider?.addEventListener('mouseleave', handleMouseLeave);
    };
  }, [sliderRef, handleSlideStart, handleKeyDown, handleMouseMove, handleMouseLeave]);

  useEffect(() => {
    setIsStartover(streamType === StreamType.StartOver);
  }, [streamType]);

  const progressPercentageValidated = () => {
    let progress = progressPercentage;

    if (isSkipping && player) {
      if (streamType === StreamType.StartOver) {
        progress = getProgressPercentage(validatedTargetPoisition, durationSeconds);
      } else {
        progress = getProgressPercentage(validatedTargetPoisition, player.getDuration());
      }
    }

    return progress;
  };

  const showMarker = isSkipping || isScrubberDragging;
  const progressSkipSeconds = isScrubberDragging
    ? dragSkipPosition
    : isSkipping
    ? validatedTargetPoisition
    : progressPercentage;

  return {
    sliderRef,
    sliderTrackRef,
    isSkipping,
    showMarker,
    progressPercentageValidated: progressPercentageValidated(),
    progressSkipSeconds,
    isStartover,
    isSliderFocused
  };
};

const clampValue = (val: number, min: number, max: number) => {
  return val > max ? max : val < min ? min : val;
};

const roundValueToStep = (value: number, step: number, min: number) => {
  const nearestStep = Math.round((value - min) / step) * step + min;

  return Number(nearestStep.toFixed(step));
};

const getPointerPosition = (event: MouseEvent | TouchEvent, touchId: number | null) => {
  if (touchId !== null && (event as TouchEvent).changedTouches) {
    for (let i = 0; i < (event as TouchEvent).changedTouches.length; i += 1) {
      const touch = (event as TouchEvent).changedTouches[i];

      if (touch.identifier === touchId) {
        return {
          x: touch.clientX,
          y: touch.clientY
        };
      }
    }

    return false;
  }

  return {
    x: (event as PointerEvent | MouseEvent).clientX,
    y: (event as PointerEvent | MouseEvent).clientY
  };
};

const getValueFromEvent = (
  event: MouseEvent | TouchEvent,
  track: HTMLDivElement | null,
  touchId: number | null,
  props: {
    min: number;
    max: number;
    step: number;
    rounded: boolean;
  }
) => {
  const position = getPointerPosition(event, touchId);

  if (!track || !position) return null;

  const { x } = position;
  const { min, max, step, rounded } = props;
  const { left, width } = track.getBoundingClientRect();

  const difference = x - left;
  const differencePercent = difference / width;
  const value = (max - min) * differencePercent + min;

  const nextValue = rounded ? roundValueToStep(value, step, min) : value;
  const clampedValue = clampValue(nextValue, min, max);

  return clampedValue;
};

export default useSlider;
